import cloneDeep from 'lodash.clonedeep';
import { isTest } from 'utils/environment';
import { LineChartWidgetData } from 'widgets/ChartWidgets/LineChartWidget/LineChartWidget.types';

import { WidgetType } from '../../../types/widget.types';
import { DEFAULT_TEXT_STYLE, chartColorPalette } from '../ChartWidget.config';
import {
  applyPatterns,
  extendSeriesData,
  formatAxisDataLabel,
  formatDataLabel,
  getPercent,
  getPrefixSuffix,
  replaceGWithBFormatter,
} from '../ChartWidget.helpers';
import { HighchartsSeriesData } from '../ChartWidget.hooks';
import { ChartDataSeries, ChartWidgetData } from '../ChartWidget.types';
import { CustomPointSeries, HighchartsOptions } from '../DataTableChartCommon/DataTableChartCommon.types';
import { DEFAULT_HEIGHT, DEFAULT_WIDTH } from './SplineChartWidget.config';
import { VERSION } from './SplineChartWidget.upgrade';

export const generateDefaultData = () => {
  return {
    widgetType: WidgetType.SplineChart,
    widgetData: {
      version: VERSION,
      widthPx: DEFAULT_WIDTH,
      heightPx: DEFAULT_HEIGHT,
      topPx: 0,
      leftPx: 0,
      rotateDeg: 0,
      altText: '',
      isDecorative: false,
      legend: {
        enabled: true,
        labelStyle: {
          ...DEFAULT_TEXT_STYLE,
        },
      },
      dataLabels: {
        enabled: false,
        style: {
          ...DEFAULT_TEXT_STYLE,
        },
      },
      seriesData: extendSeriesData([
        {
          data: ['May 2012', 'Jan 2014', 'Jul 2015', 'Oct 2017', 'Aug 2019', 'Jun 2021'],
        },
        {
          name: 'NVDA',
          data: [63, 63, 42, 64, 61, 66],
        },
        {
          name: 'JAWS',
          data: [42, 50, 40, 61, 75, 58],
        },
        {
          name: 'VoiceOver',
          data: [30, 38, 29, 40, 48, 42],
        },
      ] as ChartDataSeries),
      seriesMeta: {
        colors: chartColorPalette,
      },
      xAxis: {
        title: {
          text: 'Time',
          style: {
            ...DEFAULT_TEXT_STYLE,
            fontWeight: 'bold',
          },
        },
        style: {
          lineColor: '#000000',
          lineWidth: 1,
        },
        labelStyle: {
          ...DEFAULT_TEXT_STYLE,
        },
      },
      yAxis: {
        title: {
          text: 'Percentage usage',
          style: {
            ...DEFAULT_TEXT_STYLE,
            fontWeight: 'bold',
          },
        },
        enableLine: false,
        style: {
          lineColor: '#000000',
          lineWidth: 1,
        },
        labelStyle: {
          ...DEFAULT_TEXT_STYLE,
        },
      },
      grid: {
        style: {
          lineColor: '#e6e6e6',
        },
      },
      generalOptions: {
        innerSize: 0,
        borderWidth: 0,
        borderColor: '#FFFFFF',
      },
      patterns: {
        enabled: false,
        list: [],
      },
    } as LineChartWidgetData,
  };
};

export const generateSingleSplineDefaultData = () => {
  const data = generateDefaultData();
  return {
    ...data,
    widgetData: {
      ...data.widgetData,
      chartType: 'SingleSpline',
      seriesData: extendSeriesData([
        {
          data: ['May 2012', 'Jan 2014', 'Jul 2015', 'Oct 2017', 'Aug 2019', 'Jun 2021'],
        },
        {
          name: 'NVDA',
          data: [63, 63, 42, 64, 61, 66],
        },
      ] as ChartDataSeries),
    } as LineChartWidgetData,
  };
};

export const getHighChartsSplineConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: ChartWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
) => {
  const options = defaultOptions;

  const { enabled: patternsEnabled, list: patternsList } = widgetData.patterns;

  const {
    dataLabels: { prefix, suffix },
    numberFormat: { format, precision, separator } = {},
  } = widgetData;

  options.chart = options.chart || {};
  options.chart.events = { render: CustomLegendRender };
  options.chart.type = 'spline';

  options.plotOptions = options.plotOptions || {};
  options.plotOptions.spline = options.plotOptions.spline || {};

  options.plotOptions.spline.lineWidth = widgetData.generalOptions.lineWidth || 2;

  options.keyCustom = keyCustom;

  options.xAxis = (options.xAxis as Highcharts.XAxisOptions) || {};
  options.xAxis.categories = categories;

  options.yAxis = {
    ...((options.yAxis as Highcharts.YAxisOptions) || {}),
    labels: {
      ...((options.yAxis as Highcharts.YAxisOptions)?.labels || {}),
      formatter: function () {
        if (format === 'percentage') {
          return `${formatAxisDataLabel({
            dataLabel: replaceGWithBFormatter.call(this),
            prefix: '',
            suffix: '%',
          })}`;
        }
        return `${formatAxisDataLabel({
          dataLabel: replaceGWithBFormatter.call(this),
          prefix,
          suffix,
        })}`;
      },
    },
  };

  options.plotOptions.spline.dataLabels = {
    formatter: function () {
      if (format === 'percentage') {
        const adjustedPrecision = precision === undefined || precision === 'Auto' ? '0' : precision;
        const percent = getPercent({ series: this.series, value: this.y || 0 });
        return `${formatDataLabel({ dataLabel: percent, precision: adjustedPrecision, separator })}%`;
      }
      const { prefix: customPrefix = prefix, suffix: customSuffix = suffix } = getPrefixSuffix({
        data: this.series.data[this.point.index],
        prefix,
        suffix,
      });

      return `${formatDataLabel({
        dataLabel: this.y,
        prefix: customPrefix,
        suffix: customSuffix,
        precision,
        separator,
      })}`;
    },
  };

  options.series = series.map(({ name, color, data }) => ({
    name,
    color,
    data: cloneDeep(data), // Avoid modifying the original when applying patterns
    visible: true,
    state: { hover: { enabled: false } },
    type: 'spline',
    dashStyle: 'Solid',
    marker: {
      ...widgetData.marker,
    },
  }));

  if (patternsEnabled) {
    applyPatterns(options, patternsList);
  }

  // highcharts bug for line chart in the test environment
  if (isTest) {
    options.xAxis.tickAmount = 100;
    options.yAxis.tickAmount = 100;
  }

  return options;
};

function CustomLegendRender(this: Highcharts.Chart | undefined) {
  if (this?.legend?.allItems) {
    this.legend.allItems.forEach((item) => {
      const seriesItem = item as CustomPointSeries;
      // fixed style for legends
      seriesItem.legendItem?.line?.attr({
        'stroke-width': 2,
      });
      seriesItem.legendItem?.symbol?.attr({
        x: 5,
        y: 7,
        height: 8,
        width: 8,
      });
    });
  }
}
